import { useEffect, useMemo, useState } from 'react'
import { PaymentMethodStep } from './PaymentMethodStep'
import { apiUrl } from '../lib/api'
import { getPenzionExternalUrl } from '../lib/booking'

/**
 * Rezervační flow modal — Schlosspark styl (square corners, white/ink/red).
 * 5 kroků:
 *   1) calendar — měsíční view, vyber datum (stay = rozsah od/do; hourly = jediný den)
 *   2) hours    — jen pro hourly: hodinové sloty 09–10, 10–11, …
 *   3) form     — jméno + telefon
 *   4) summary  — souhrn + volba platby
 *   5) done     — potvrzeno (po GoPay redirectu nebo skip platby)
 */

type Kind = 'stay' | 'hourly'

type OpenDetail = {
  type: string
  kind: Kind
  label: string
  from_dt?: string
  to_dt?: string
  persons?: number      // z vyhledávací lišty (v2)
}

const HOUR_SLOTS = [
  { start: '09:00', end: '10:00' },
  { start: '10:00', end: '11:00' },
  { start: '11:00', end: '12:00' },
  { start: '12:00', end: '13:00' },
  { start: '13:00', end: '14:00' },
  { start: '14:00', end: '15:00' },
  { start: '15:00', end: '16:00' },
  { start: '16:00', end: '17:00' },
  { start: '17:00', end: '18:00' },
  { start: '18:00', end: '19:00' },
  { start: '19:00', end: '20:00' },
  { start: '20:00', end: '21:00' },
]

// Plovoucí sauna — 2 pevné dvouhodinové bloky denně, mezi nimi půlhodina
// na vyvětrání a dotopení (musí souhlasit se SLOT_STARTS v backendu).
const SAUNA_SLOTS = [
  { start: '15:00', end: '17:00' },
  { start: '17:30', end: '19:30' },
]

/** Časové sloty podle typu — sauna má vlastní, kurty hodinové. */
function slotsForType(t: string) {
  return t === 'sauna' ? SAUNA_SLOTS : HOUR_SLOTS
}
/** „HH:MM" + n hodin (vícehodinové rezervace kurtů). */
function addHours(hhmm: string, n: number): string {
  const [h, m] = hhmm.split(':').map(Number)
  return `${String(h + n).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
const SPORT_CLOSE = '21:00'   // kurty do 21:00 (musí souhlasit se SPORT_CLOSE_HOUR v backendu)

// Bivak (kemp) — pevné 12h sloty (musí souhlasit s KEMP_SLOTS v backendu)
const KEMP_SLOTS = [
  { id: '07:00-19:00', label: 'Denní', time: '7:00 – 19:00', start: '07:00' },
  { id: '19:00-07:00', label: 'Noční', time: '19:00 – 7:00', start: '19:00' },
]

// Časy příjezdu/odjezdu podle typu (pravidla areálu)
const STAY_TIMES: Record<string, string> = {
  penzion: 'Příjezd od 14:00 · odjezd do 10:00',
  chatka: 'Příjezd od 16:00 · odjezd do 14:00 — rybařit můžete až do 16:00',
  tinyhouse: 'Příjezd od 15:00 · odjezd do 12:00',
  tiny_house: 'Příjezd od 15:00 · odjezd do 12:00',
  glamping_tiny: 'Příjezd od 15:00 · odjezd do 12:00',
  karavan: 'Příjezd od 12:00 · odjezd do 12:00',
  kemp: 'Slot 12 h — 7:00–19:00 nebo 19:00–7:00',
}

// Časy do souhrnu (musí souhlasit se STAY_TIMES v backendu)
const STAY_CHECKIN: Record<string, string> = {
  penzion: 'od 14:00', chatka: 'od 16:00', tinyhouse: 'od 15:00',
  tiny_house: 'od 15:00', glamping_tiny: 'od 15:00', karavan: 'od 12:00',
}
const STAY_CHECKOUT: Record<string, string> = {
  penzion: 'do 10:00', chatka: 'do 14:00', tinyhouse: 'do 12:00',
  tiny_house: 'do 12:00', glamping_tiny: 'do 12:00', karavan: 'do 12:00',
}

const CZ_MONTHS = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec']
const CZ_DAYS = ['Po','Út','St','Čt','Pá','So','Ne']

// Sloučené karty na webu = 1 položka, ale rezervují víc fyzických typů zdrojů.
// "Glamping/Tiny house" ukáže v kroku výběru Tiny house + Glamping A/B,
// každá jednotka se pak zarezervuje pod svým skutečným typem (= správná cena).
const COMBINED_TYPES: Record<string, string[]> = {
  glamping_tiny: ['tinyhouse'],
}

// Placené předem přes bránu (stejný seznam jako PREPAID_TYPES na serveru)
const PREPAID_TYPES = new Set(['tenis', 'beach', 'sauna', 'chatka', 'tinyhouse', 'tiny_house', 'glamping', 'kemp', 'karavan', 'penzion'])
const PAY_GATE_MSG = 'Online rezervaci s platbou kartou spustíme v nejbližších dnech. Zatím prosím rezervujte telefonicky na +420 775 140 705.'

export function ReservationFlow() {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState<string>('penzion')
  const [kind, setKind] = useState<Kind>('stay')
  const [label, setLabel] = useState<string>('Pokoj penzionu')

  type Step = 'calendar' | 'form' | 'payment' | 'done'
  const [step, setStep] = useState<Step>('calendar')

  // Typy které mají konkrétní čísla zdrojů (vynechme kemp — sdílená louka)
  const HAS_RESOURCES = ['penzion','chatka','tinyhouse','glamping','glamping_tiny','karavan','kemp','tenis','beach','sauna','akce']

  // calendar state
  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [from, setFrom] = useState<string | null>(null)
  const [to, setTo] = useState<string | null>(null)

  // hours state (jen hourly)
  const [slot, setSlot] = useState<string | null>(null)
  const [hours, setHours] = useState(1)   // tenis/beach: délka 1–4 h (Robin 26. 10.: ne jen jedna hodina)

  // bivak: 12h sloty — slot příjezdu a odjezdu (pobyt = násobek 12 h)
  const [mapOpen, setMapOpen] = useState(false)           // mapa lovných míst
  const [kempArrive, setKempArrive] = useState('07:00')   // 07:00 | 19:00
  const [kempDepart, setKempDepart] = useState('19:00')   // 07:00 | 19:00
  /** Počet 12h bloků mezi zvoleným příjezdem a odjezdem (0 = neplatné). */
  function kempBlocks(): number {
    if (!from) return 0
    const f = new Date(`${from}T${kempArrive}:00`)
    const t = new Date(`${to || from}T${kempDepart}:00`)
    const h = (t.getTime() - f.getTime()) / 3600000
    if (h <= 0 || h % 12 !== 0) return 0
    return h / 12
  }

  // resource (konkrétní chatky/pokoje/kurty — multi-select pro skupiny)
  type Resource = { id: number; code: string; label: string; notes?: string; available: boolean; capacity_persons: number; type?: string; booked?: { start: string; end: string }[] }
  const [resources, setResources] = useState<Resource[]>([])
  const [resourcesLoading, setResourcesLoading] = useState(false)
  const [resourceIds, setResourceIds] = useState<number[]>([])  // multi-select
  const isCombined = !!COMBINED_TYPES[type]  // sloučená karta (Glamping/Tiny house) = jednotky různých typů
  const isKemp = type === 'kemp'             // bivak — 12h sloty místo nocí
  function toggleResource(id: number) {
    // Sloučená karta = jen 1 jednotka (různé typy/ceny se nemíchají do jedné rezervace)
    if (isCombined) {
      setResourceIds((prev) => (prev.includes(id) ? [] : [id]))
      return
    }
    setResourceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }
  const resourceLabels = resources
    .filter((r) => resourceIds.includes(r.id))
    .map((r) => r.label)
    .join(' · ')

  // Sport košík — víc termínů v jedné rezervaci („Přidat další den")
  type CartItem = { date: string; slot: string; resourceIds: number[]; resourceLabels: string; hours?: number }
  const [cart, setCart] = useState<CartItem[]>([])

  // form
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  // Robin 5. 11. 2026: u ubytování povinný e-mail (dohledání hosta, cizinecká policie, platební brána)
  const [email, setEmail] = useState('')
  const [persons, setPersons] = useState('2')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // result (vrácený backendem)
  const [estimatedCzk, setEstimatedCzk] = useState(0)
  const [reservationId, setReservationId] = useState<number | null>(null)

  // v2: voucher + sport metadata
  const [voucherCode, setVoucherCode] = useState('')
  const [voucherOpen, setVoucherOpen] = useState(false)
  const [electricity, setElectricity] = useState(false)
  const [addons, setAddons] = useState<string[]>([])  // chatka: podberak/vanicka/sak
  const [resMeta, setResMeta] = useState<{
    variable_symbol?: string
    expires_at?: string
    reschedule_token?: string
    voucher_pct?: number
  }>({})

  useEffect(() => {
    function onOpen(e: Event) {
      const detail = (e as CustomEvent<OpenDetail>).detail
      // Penzion dočasně jen přes Booking.com (Robin 26. 10.) — modal pro pokoj neotvírat
      if (detail.type === 'penzion' && getPenzionExternalUrl()) {
        window.open(getPenzionExternalUrl()!, '_blank', 'noopener')
        return
      }
      setType(detail.type)
      setKind(detail.kind)
      setLabel(detail.label)
      setFrom(detail.from_dt || null)
      setTo(detail.to_dt || null)
      // Kalendář otevři v měsíci předvyplněného termínu (z vyhledávací lišty)
      if (detail.from_dt) {
        const d = new Date(detail.from_dt.slice(0, 10) + 'T12:00:00')
        if (!isNaN(d.getTime())) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()) }
      }
      setSlot(null)
      setKempArrive('07:00')
      setKempDepart('19:00')
      setName('')
      setPhone('')
      setEmail('')
      setPersons(String(detail.persons && detail.persons > 0 ? detail.persons : 2))
      setErr(null)
      setEstimatedCzk(0)
      setReservationId(null)
      setResourceIds([])
      setResources([])
      setCart([])
      setVoucherCode('')
      setVoucherOpen(false)
      setElectricity(false)
      setAddons([])
      setResMeta({})
      setStep('calendar')
      setOpen(true)
    }
    document.addEventListener('sobacov:open-reservation', onOpen as EventListener)
    return () => document.removeEventListener('sobacov:open-reservation', onOpen as EventListener)
  }, [])

  // Platební brána: bez ní se placené rezervace online nepřijímají (server vrací 503)
  const [payLive, setPayLive] = useState<boolean | null>(null)
  useEffect(() => {
    if (!open) return
    fetch(apiUrl('/api/sobacov/payments/status'))
      .then((r) => r.json())
      .then((j) => setPayLive(!!j.live))
      .catch(() => setPayLive(null))
  }, [open])
  const payGateClosed = payLive === false && PREPAID_TYPES.has(type)

  // Rod jednotky podle typu — aby „volný/volná/volné" souhlasilo
  // (pokoj = m, chatka = f, stání = n, slot = m, hodina = f)
  const FREE_GENDER: Record<string, 'm' | 'f' | 'n'> = {
    penzion: 'm', chatka: 'f', tinyhouse: 'm', tiny_house: 'm',
    glamping: 'm', glamping_tiny: 'm', karavan: 'n', kemp: 'm',
    tenis: 'f', beach: 'f', sauna: 'm',  // blok
  }
  function freeWord(n: number): string {
    const g = FREE_GENDER[type] || 'n'
    if (n === 1) return g === 'm' ? 'volný' : g === 'f' ? 'volná' : 'volné'
    if (n <= 4) return g === 'n' ? 'volná' : 'volné'
    return 'volných'
  }

  // ── Obsazenost v kalendáři ───────────────────────────────────────
  // Dřív se zákazník dozvěděl „v tomto termínu je obsazeno" až po kliknutí
  // na Pokračovat. Teď je volno vidět přímo na dnech.
  type DayAvail = { total: number; free: number }
  const [monthAvail, setMonthAvail] = useState<Record<string, DayAvail>>({})
  const availType = COMBINED_TYPES[type] ? COMBINED_TYPES[type].join(',') : type

  useEffect(() => {
    if (!open) return
    let dead = false
    const url = apiUrl(`/api/sobacov/availability/month?resource_type=${availType}`
      + `&year=${viewYear}&month=${viewMonth + 1}`)
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!dead && j) setMonthAvail((prev) => ({ ...prev, ...j.days })) })
      .catch(() => { /* bez obarvení, rezervaci to neblokuje */ })
    return () => { dead = true }
  }, [open, availType, viewYear, viewMonth])

  /** Je v rozsahu od–do noc, která je plně obsazená? */
  function fullNightInRange(): string | null {
    if (!from) return null
    const end = to || from
    for (let d = new Date(`${from}T12:00:00`); d < new Date(`${end}T12:00:00`); d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10)
      if (monthAvail[iso] && monthAvail[iso].free === 0) return iso
    }
    // jednodenní výběr (bivak / sport) — kontroluj ten den
    if (!to || from === to) {
      const a = monthAvail[from]
      if (a && a.free === 0) return from
    }
    return null
  }

  // Výběr jednotky / hodinová mřížka se načítají hned po výběru termínu —
  // krok „Pokračovat" už neexistuje, vše je v jednom okně pod kalendářem.
  useEffect(() => {
    if (!open || !from) return
    if (kind === 'hourly') { setSlot(null); setResourceIds([]); loadDayGrid(from); return }
    if (isKemp) { setResourceIds([]); loadAvailability(); return }
    if (to && HAS_RESOURCES.includes(type)) { setResourceIds([]); loadAvailability() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, from, to])

  // Ceník z backendu — příplatky v popiscích musí být ty účtované
  const [priceList, setPriceList] = useState<Record<string, number>>({})
  useEffect(() => {
    fetch(apiUrl('/api/sobacov/web/prices'))
      .then((r) => r.json())
      .then((j) => setPriceList(j.prices || {}))
      .catch(() => { /* popisky spadnou na výchozí */ })
  }, [])

  // ── Živá cena ────────────────────────────────────────────────────
  // Počítá ji backend (/quote) stejnými funkcemi, jakými ji pak účtuje.
  // Druhá implementace v JS by se dřív nebo později rozešla s účtovanou
  // cenou — to už se jednou stalo (web 1 790 vs. účtováno 1 500).
  type Quote = { price_czk: number; lines: { units: number; nights: number; hours: number; unit_price_czk: number }[]; voucher_pct?: number }
  const [quote, setQuote] = useState<Quote | null>(null)
  const [quoteBusy, setQuoteBusy] = useState(false)
  const quoteKey = JSON.stringify([
    type, kind, from, to, slot, kempArrive, kempDepart, resourceIds,
    electricity, addons, cart, persons, voucherCode.trim().toUpperCase(),
    resources.length,
  ])

  useEffect(() => {
    // Zavřený modal se nerenderuje až k `guests`/`maxGuests`, takže
    // buildQuoteBody() tu nesmí běžet (sáhlo by na neinicializovanou konstantu).
    if (!open) { setQuote(null); return }
    const body = buildQuoteBody()
    if (!body) { setQuote(null); return }
    let dead = false
    setQuoteBusy(true)
    const t = setTimeout(async () => {
      try {
        const r = await fetch(apiUrl('/api/sobacov/quote'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        if (!r.ok) throw new Error('quote')
        const j = await r.json()
        if (!dead) setQuote({ price_czk: j.price_czk, lines: j.lines || [], voucher_pct: j.voucher_pct })
      } catch {
        if (!dead) setQuote(null)   // cena se nezobrazí, rezervaci to neblokuje
      } finally {
        if (!dead) setQuoteBusy(false)
      }
    }, 220)
    return () => { dead = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey, open])

  // ── Kalendář helpery ─────────────────────────────────────────────
  // POZN: useMemo MUSÍ být před `if (!open) return null` (Rules of Hooks)
  const days = useMemo(() => buildMonth(viewYear, viewMonth), [viewYear, viewMonth])

  if (!open) return null

  function close() {
    setOpen(false)
  }
  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11); setViewYear(viewYear - 1)
    } else setViewMonth(viewMonth - 1)
  }
  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0); setViewYear(viewYear + 1)
    } else setViewMonth(viewMonth + 1)
  }

  function pickDay(iso: string) {
    if (kind === 'hourly') {
      setFrom(iso); setTo(iso)
      return
    }
    if (!from || (from && to)) {
      setFrom(iso); setTo(null)
    } else {
      if (iso < from) {
        setTo(from); setFrom(iso)
      } else if (iso === from) {
        // nic
      } else {
        setTo(iso)
      }
    }
  }

  function inRange(iso: string) {
    if (!from) return false
    if (kind === 'hourly') return iso === from
    if (!to) return iso === from
    return iso >= from && iso <= to
  }

  // Tiny house: minimálně 2 noci (validuje i backend)
  const isTiny = type === 'glamping_tiny' || type === 'tinyhouse' || type === 'tiny_house'
  function nightsSelected(): number {
    if (!from || !to) return 0
    return Math.round((new Date(to + 'T12:00:00').getTime() - new Date(from + 'T12:00:00').getTime()) / 86400000)
  }
  function canContinueFromCalendar() {
    if (!from) return false
    if (fullNightInRange()) return false   // v rozsahu je plně obsazená noc
    if (kind === 'hourly') return true
    if (isKemp) return true          // bivak: stačí 1 den (přes noc doplní slot)
    if (!to) return false
    if (isTiny && nightsSelected() < 2) return false
    return true
  }

  async function loadAvailability() {
    if (isKemp) {
      // bivak — dostupnost pro přesný rozsah 12h slotů
      // POZOR: /availability bere jen datum (YYYY-MM-DD), ne čas
      setResourcesLoading(true)
      try {
        const r = await fetch(apiUrl(`/api/sobacov/availability?resource_type=kemp&from_dt=${from}&to_dt=${to || from}`))
        if (r.ok) setResources((await r.json()).resources || [])
      } catch {}
      finally { setResourcesLoading(false) }
      return
    }
    if (!from || !to) return
    setResourcesLoading(true)
    try {
      // Sloučená karta načte zdroje všech svých typů najednou (tinyhouse,glamping)
      const rt = COMBINED_TYPES[type] ? COMBINED_TYPES[type].join(',') : type
      const r = await fetch(apiUrl(`/api/sobacov/availability?resource_type=${rt}&from_dt=${from}&to_dt=${to}`))
      if (r.ok) {
        const j = await r.json()
        setResources(j.resources || [])
      }
    } catch {}
    finally { setResourcesLoading(false) }
  }

  // Denní mřížka obsazenosti (tenis/beach) — kurty × 1h sloty pro vybraný den
  async function loadDayGrid(date: string | null = from) {
    if (!date) return
    setResourcesLoading(true)
    setResources([])
    try {
      const r = await fetch(apiUrl(`/api/sobacov/day-grid?resource_type=${type}&date=${date}`))
      if (r.ok) {
        const j = await r.json()
        setResources((j.resources || []).map((x: Resource) => ({ ...x, available: true })))
      }
    } catch {}
    finally { setResourcesLoading(false) }
  }

  // Je daný kurt v daném slotu obsazený? (časy "HH:MM", překryv intervalů)
  function slotBooked(r: Resource, s: { start: string; end: string }) {
    return (r.booked || []).some((b) => b.start < s.end && b.end > s.start)
  }

  // Klik na buňku mřížky: jiný slot = přepni slot + jen tento kurt;
  // stejný slot = přidej/odeber kurt (skupina na stejný čas).
  function pickCell(courtId: number, slotId: string) {
    if (slot !== slotId) {
      setSlot(slotId)
      setResourceIds([courtId])
    } else {
      toggleResource(courtId)
    }
  }

  // Z mřížky: ulož vybraný termín do košíku a pokračuj na formulář
  function commitDayPick() {
    if (!from || !slot || resourceIds.length === 0) return
    const labels = resources.filter((r) => resourceIds.includes(r.id)).map((r) => r.label).join(', ')
    setCart((prev) => [...prev, { date: from, slot, resourceIds: [...resourceIds], resourceLabels: labels, hours: type === 'sauna' ? undefined : hours }])
    setSlot(null)
    setResourceIds([])
    setStep('form')
  }

  // „Přidat další den" — vyčisti rozpracovaný výběr a zpět na kalendář
  function addAnotherDay() {
    setFrom(null)
    setTo(null)
    setSlot(null)
    setResourceIds([])
    setStep('calendar')
  }

  function removeCartItem(idx: number) {
    setCart((prev) => prev.filter((_, i) => i !== idx))
  }

  // Zpět z formuláře (sport): vrať poslední termín do mřížky k úpravě
  function backFromHourlyForm() {
    if (cart.length > 0) {
      const last = cart[cart.length - 1]
      setCart((prev) => prev.slice(0, -1))
      setFrom(last.date)
      setSlot(last.slot)
      setResourceIds(last.resourceIds)
      loadDayGrid(last.date)
      setStep('calendar')
    } else {
      setStep('calendar')
    }
  }

  /** Výběr jednotky se ukáže, jakmile je termín kompletní a volný. */
  const showUnitPicker =
    kind === 'stay' && HAS_RESOURCES.includes(type) && !!from
    && (isKemp ? kempBlocks() > 0 : !!to)
    && !fullNightInRange()

  /** Krok 1 je hotový: termín + (kde je potřeba) konkrétní místo/čas. */
  function canProceedStep1(): boolean {
    if (!canContinueFromCalendar()) return false
    if (kind === 'hourly') return !!slot && resourceIds.length > 0
    if (isKemp) return kempBlocks() > 0 && resourceIds.length > 0
    if (HAS_RESOURCES.includes(type)) return resourceIds.length > 0
    return true
  }

  /** Co hostu ještě chybí — jedna věta pod tlačítkem. */
  function step1Hint(): string {
    if (!from) return ''
    if (kind === 'hourly') return slot && resourceIds.length ? '' : 'Vyberte hodinu v mřížce níže.'
    if (isKemp) {
      if (kempBlocks() === 0) return 'Zvolte čas příjezdu a odjezdu.'
      return resourceIds.length ? '' : 'Vyberte místo pro bivak níže.'
    }
    if (!to) return ''
    if (fullNightInRange()) return ''
    if (HAS_RESOURCES.includes(type) && !resourceIds.length) return `Vyberte ${resourceCategoryLabel(type)} níže.`
    return ''
  }

  function proceedStep1() {
    if (!canProceedStep1()) return
    if (kind === 'hourly') { commitDayPick(); return }   // uloží termín do košíku a jde na údaje
    setStep('form')
  }

  function canSubmitForm() {
    return name.trim().length >= 2 && phone.trim().length >= 9
  }

  /**
   * Nejvyšší možný počet hostů — součet kapacit vybraných jednotek, jinak
   * kapacita největší jednotky daného typu (chatka 4, pokoj 2–4 …).
   * Dřív nabídka vždy šla do 8, i když se do chatky vejdou 4.
   */
  const maxGuests = (() => {
    const picked = resources.filter((r) => resourceIds.includes(r.id))
    if (picked.length) return Math.max(1, picked.reduce((s, r) => s + (r.capacity_persons || 0), 0))
    if (resources.length) return Math.max(1, ...resources.map((r) => r.capacity_persons || 0))
    return 8
  })()

  /** Počet hostů oříznutý kapacitou — nepoužívat `persons` přímo.
   *  (Ořez děláme při čtení, ne přes useEffect: tenhle kód je až za
   *  `if (!open) return null`, takže tu hook použít nelze.) */
  const guests = Math.min(Math.max(1, parseInt(persons, 10) || 1), maxGuests)

  /** Popis pod cenou — z čeho se částka skládá. */
  function quoteDetail(): string {
    if (!quote || !quote.lines.length) {
      if (kind === 'hourly') return 'Vyberte den a hodinu'
      if (isKemp) return 'Vyberte den a slot'
      return isTiny ? 'Vyberte termín — min. 2 noci' : 'Vyberte termín příjezdu a odjezdu'
    }
    const units = quote.lines.reduce((a, l) => a + l.units, 0)
    const unitWord = kind === 'hourly'
      ? (type === 'tenis' ? 'kurt' : 'hřiště')
      : type === 'penzion' ? 'pokoj' : type === 'chatka' ? 'chatka' : 'jednotka'
    if (type === 'sauna') {
      const n = quote.lines.length
      return `${n}× blok · 120 minut · celá sauna jen pro vás`
    }
    if (kind === 'hourly') {
      const h = quote.lines.length
      return `${h}× hodina · ${units} ${unitWord}${units > 1 ? 'y' : ''}`
    }
    if (isKemp) {
      const blocks = Math.max(1, Math.round(quote.lines.reduce((a, l) => a + l.hours, 0) / 12))
      return `${blocks}× 12h slot${units > 1 ? ` · ${units} místa` : ''}`
    }
    const nights = quote.lines[0]?.nights || 0
    return `${nights} ${nights === 1 ? 'noc' : nights < 5 ? 'noci' : 'nocí'}`
      + (units > 1 ? ` · ${units} ${unitWord}${unitWord === 'chatka' ? 'y' : 'e'}` : '')
  }

  /** Skutečný backend typ — sloučená karta (Glamping/Tiny house) ho nemá. */
  function effectiveType(): string {
    if (!isCombined) return type
    const picked = resources.find((r) => resourceIds.includes(r.id))
    return picked?.type || COMBINED_TYPES[type][0]
  }

  /**
   * Tělo dotazu na /quote — musí odpovídat tomu, co pošle `submit()`,
   * jinak by zobrazená cena nebyla ta účtovaná. null = ještě není co počítat.
   */
  function buildQuoteBody(): Record<string, unknown> | null {
    const rt = effectiveType()
    const b: Record<string, unknown> = { reservation_type: rt }
    if (guests > 0) b.num_persons = guests
    if (voucherCode.trim()) {
      b.voucher_code = voucherCode.trim().toUpperCase()
      if (phone.trim()) b.guest_phone = phone.trim()
    }
    if (electricity && (rt === 'karavan' || rt === 'kemp')) b.electricity = true
    if ((rt === 'chatka' || rt === 'kemp') && addons.length) b.addons = addons

    if (isKemp) {
      if (!from) return null
      if (kempBlocks() > 0) {
        b.from_dt = `${from}T${kempArrive}:00`
        b.to_dt = `${to || from}T${kempDepart}:00`
      } else {
        b.from_dt = from
        b.hour_start = kempArrive
      }
      b.units = Math.max(1, resourceIds.length)
      return b
    }
    if (kind === 'hourly') {
      if (cart.length > 0) {
        b.segments = cart.map((c) => ({
          from_dt: c.date,
          hour_start: c.slot.split('-')[0],
          units: Math.max(1, c.resourceIds.length),
          hours: c.hours,
        }))
        return b
      }
      if (!from || !slot) return null
      b.from_dt = from
      b.hour_start = slot.split('-')[0]
      b.units = Math.max(1, resourceIds.length)
      if (type !== 'sauna') b.hours = hours
      return b
    }
    if (!from || !to) return null
    if (isTiny && nightsSelected() < 2) return null   // backend by vrátil 400
    b.from_dt = from
    b.to_dt = to
    b.units = Math.max(1, resourceIds.length)
    return b
  }

  async function submit(payNow: boolean) {
    if (kind === 'hourly' && cart.length === 0) {
      setErr('Vyberte alespoň jeden termín.')
      return
    }
    // Robin 5. 11. 2026: placené rezervace jen přes platební bránu — bez ní online nepřijímat
    if (payGateClosed && !voucherCode.trim()) {
      setErr(PAY_GATE_MSG)
      return
    }
    if (kind !== 'hourly' && !/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(email.trim())) {
      setErr('U rezervace ubytování vyplňte prosím platný e-mail.')
      return
    }
    setBusy(true)
    setErr(null)
    try {
      // Sloučená karta nemá vlastní backend typ — vezmi skutečný typ vybrané jednotky
      let reservationType = type
      if (isCombined) {
        const picked = resources.find((r) => resourceIds.includes(r.id))
        reservationType = picked?.type || COMBINED_TYPES[type][0]
      }
      const body: any = {
        reservation_type: reservationType,
        guest_name: name.trim(),
        guest_phone: phone.trim(),
        from_dt: from,
        to_dt: to,
        num_persons: guests,
        online_discount: false,
      }
      if (email.trim()) body.guest_email = email.trim()
      if (voucherCode.trim()) body.voucher_code = voucherCode.trim().toUpperCase()
      if (electricity && (reservationType === 'karavan' || reservationType === 'kemp')) body.electricity = true
      if ((reservationType === 'chatka' || reservationType === 'kemp') && addons.length) body.addons = addons
      if (isKemp) {
        // Bivak: plný rozsah 12h slotů (může být i několik dní)
        body.from_dt = `${from}T${kempArrive}:00`
        body.to_dt = `${to || from}T${kempDepart}:00`
        if (resourceIds.length === 1) body.resource_id = resourceIds[0]
        else if (resourceIds.length > 1) body.resource_ids = resourceIds
      } else if (kind === 'hourly') {
        // víc termínů (košík) → jedna dávka, master drží součet
        body.from_dt = cart[0].date
        body.to_dt = cart[0].date
        body.hour_start = cart[0].slot.split('-')[0]
        if (cart[0].hours) body.hours = cart[0].hours
        body.segments = cart.map((c) => ({
          from_dt: c.date,
          hour_start: c.slot.split('-')[0],
          resource_ids: c.resourceIds,
          hours: c.hours,
        }))
      } else {
        if (resourceIds.length === 1) body.resource_id = resourceIds[0]
        else if (resourceIds.length > 1) body.resource_ids = resourceIds
      }
      const r = await fetch(apiUrl('/api/sobacov/reservations/public'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.detail || 'Nepodařilo se odeslat')
      }
      const j = await r.json()
      setReservationId(j.id)
      const base = j.price_czk ?? j.estimated_price_czk ?? 0
      setEstimatedCzk(base)
      setResMeta({
        variable_symbol: j.variable_symbol,
        expires_at: j.expires_at,
        reschedule_token: j.reschedule_token,
        voucher_pct: j.voucher_pct || 0,
      })

      // Voucher 100 % nebo bezplatná rezervace → rovnou done (BE už ji confirmnul)
      if (j.requires_payment === false || base <= 0) {
        setStep('done')
        return
      }

      // Vše se platí 100 % předem online přes bránu (od 18. 8. 2026 i ubytování).
      setStep('payment')
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[100] bg-steel-900/85 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      {/* Radial brand-tinted glow behind modal */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-brand-500/10 blur-[120px]" />
      </div>

      <div className="relative bg-white w-full sm:max-w-xl max-h-[95vh] overflow-y-auto shadow-[0_30px_80px_-20px_rgba(229,2,69,0.35),0_10px_40px_-10px_rgba(0,0,0,0.4)] ring-1 ring-white/10 animate-fade-up">
        {/* Header — gradient + court line pattern + brand accent strip */}
        <div className="relative bg-gradient-to-r from-steel-800 via-steel-700 to-steel-600 text-white px-6 md:px-7 pt-5 pb-4 sticky top-0 z-10 overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-500 to-brand-700" />
          <div className="absolute right-0 -top-12 -mr-12 w-44 h-44 rounded-full bg-brand-500/15 blur-2xl" />
          {/* Subtle court line pattern — diagonal grid */}
          <svg
            className="absolute inset-0 w-full h-full text-white/[0.04] pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <pattern id="court-lines" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="40" stroke="currentColor" strokeWidth="1" />
                <line x1="0" y1="0" x2="40" y2="0" stroke="currentColor" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#court-lines)" />
          </svg>

          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.30em] text-brand-300 font-bold flex items-center gap-2">
                <span className="inline-block w-5 h-px bg-brand-400" />
                Rezervace · {stepLabel(step, kind)}
              </div>
              <div className="text-xl md:text-2xl font-bold tracking-tight mt-1 truncate">
                {label}
              </div>
            </div>
            <button
              onClick={close}
              className="shrink-0 w-9 h-9 grid place-items-center bg-white/10 hover:bg-white/25 border border-white/15 text-white text-lg backdrop-blur-sm transition-colors"
              aria-label="Zavřít"
            >
              ✕
            </button>
          </div>

          {/* Step indicator — numbered dots s connectorem */}
          <div className="relative mt-4 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.20em] font-bold">
            {(kind === 'hourly'
              ? ['calendar', 'form', 'payment']
              : ['calendar', 'form']
            ).map((s, i, arr) => {
              const isActive = step === s
              const isPast = arr.indexOf(step) > i
              const dotCls = isActive
                ? 'bg-brand-500 text-white ring-2 ring-brand-300/40'
                : isPast
                ? 'bg-brand-400/80 text-white'
                : 'bg-white/10 text-white/50 border border-white/20'
              return (
                <span key={s} className="flex items-center gap-1.5">
                  <span className={`w-5 h-5 grid place-items-center text-[10px] ${dotCls} transition-colors`}>
                    {i + 1}
                  </span>
                  {i < arr.length - 1 && (
                    <span className={`w-4 h-px ${isPast ? 'bg-brand-400/80' : 'bg-white/15'}`} />
                  )}
                </span>
              )
            })}
          </div>

          {/* Živá cena — vidět od prvního kliknutí, ne až u platby */}
          <div className="relative mt-3.5 pt-3 border-t border-white/10 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.20em] text-white/50 font-bold">
                {step === 'payment' || step === 'done' ? 'K platbě' : 'Cena'}
              </div>
              <div className="text-[11px] text-white/70 truncate mt-0.5">
                {step === 'payment' || step === 'done'
                  ? 'včetně všech příplatků'
                  : quoteDetail()}
              </div>
            </div>
            <div className="shrink-0 text-right">
              {step === 'payment' || step === 'done' ? (
                <span className="text-2xl font-bold tabular-nums">
                  {estimatedCzk.toLocaleString('cs')} <span className="text-sm font-semibold">Kč</span>
                </span>
              ) : quote ? (
                <span className={`text-2xl font-bold tabular-nums transition-opacity ${quoteBusy ? 'opacity-50' : ''}`}>
                  {quote.price_czk.toLocaleString('cs')} <span className="text-sm font-semibold">Kč</span>
                </span>
              ) : (
                <span className="text-[11px] text-white/50">
                  {quoteBusy ? 'počítám…' : '—'}
                </span>
              )}
              {!!quote?.voucher_pct && step !== 'payment' && step !== 'done' && (
                <div className="text-[10px] text-brand-300 font-bold uppercase tracking-wider">
                  voucher −{quote.voucher_pct} %
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Body — fade-up transition on každý step change */}
        <div className="p-5 md:p-7 animate-fade-up" key={step}>
          {payGateClosed && (step === 'calendar' || step === 'form') && (
            <div className="mb-5 border border-sun-400 bg-sun-50 rounded-[10px] p-4 text-sm text-ink-900">
              <div className="font-bold">Online rezervace s platbou se spouští</div>
              <p className="mt-1 text-ink-700">
                Platbu kartou zapínáme v nejbližších dnech. Do té doby vám termín rád zarezervujeme po telefonu.
              </p>
              <a href="tel:+420775140705" className="btn-book mt-3 !px-5 !py-2 !text-[14px]">Zavolat +420 775 140 705</a>
            </div>
          )}
          {/* STEP 1: Calendar */}
          {step === 'calendar' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <button onClick={prevMonth} className="w-10 h-10 grid place-items-center hover:bg-brand-50 hover:border-brand-300 hover:text-brand-600 border border-ink-900/10 text-ink-900 transition-colors text-lg">‹</button>
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-brand-500 font-bold">Měsíc</div>
                  <div className="font-bold uppercase tracking-wider text-ink-900 text-sm mt-0.5">
                    {CZ_MONTHS[viewMonth]} <span className="text-ink-700/60 font-normal">{viewYear}</span>
                  </div>
                </div>
                <button onClick={nextMonth} className="w-10 h-10 grid place-items-center hover:bg-brand-50 hover:border-brand-300 hover:text-brand-600 border border-ink-900/10 text-ink-900 transition-colors text-lg">›</button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-ink-700 font-semibold mb-2">
                {CZ_DAYS.map((d) => <div key={d}>{d}</div>)}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => {
                  if (!d) return <div key={i} />
                  const sel = inRange(d.iso)
                  const isFrom = d.iso === from
                  const isTo = d.iso === to
                  const todayIso = new Date().toISOString().slice(0,10)
                  const isToday = d.iso === todayIso
                  const past = d.iso < todayIso
                  // víkend: index v gridu modulo 7 (cz: po=0, ne=6)
                  const weekIdx = i % 7
                  const isWeekend = weekIdx === 5 || weekIdx === 6
                  // Obsazenost dne — plný den nejde vybrat, zbytek se odstíní
                  const av = monthAvail[d.iso]
                  const full = !!av && av.free === 0
                  const few = !!av && av.free > 0 && av.free <= Math.max(1, Math.floor(av.total * 0.3))
                  return (
                    <button
                      key={i}
                      disabled={past || full}
                      title={av ? (full ? 'Obsazeno' : `Volné: ${av.free} z ${av.total}`) : undefined}
                      onClick={() => pickDay(d.iso)}
                      className={`relative min-h-[58px] sm:min-h-[66px] flex flex-col items-center justify-center text-sm font-medium transition-all duration-150 ease-out border active:scale-95 ${
                        past ? 'text-ink-700/25 border-transparent cursor-not-allowed' :
                        full ? 'bg-ink-900/[0.06] text-ink-700/35 border-ink-900/10 cursor-not-allowed line-through decoration-ink-700/40' :
                        sel ? 'bg-gradient-to-br from-brand-500 to-brand-700 text-white border-brand-700 font-bold shadow-md shadow-brand-700/30 scale-[1.02]' :
                        few ? 'hover:bg-brand-50 hover:border-brand-300 border-amber-300 bg-amber-50/60 text-ink-800' :
                        isWeekend ? 'hover:bg-brand-50 hover:border-brand-300 border-ink-900/10 bg-steel-50/40 text-ink-800' :
                        'hover:bg-brand-50 hover:border-brand-300 border-ink-900/10 text-ink-800'
                      } ${isFrom || isTo ? 'ring-2 ring-brand-500 ring-offset-1' : ''} ${
                        isToday && !sel ? 'ring-1 ring-brand-400 ring-offset-1' : ''
                      }`}
                    >
                      <span className="text-[19px] leading-none font-semibold">{d.day}</span>
                      {!past && av && (
                        <span className={`mt-1 px-0.5 text-center text-[10.5px] leading-[1.15] font-bold ${
                          sel ? 'text-white/90'
                          : full ? 'text-ink-700/45'
                          : few ? 'text-amber-700'
                          : 'text-ink-700/70'
                        }`}>
                          {full ? 'plno' : `${av.free} ${freeWord(av.free)}`}
                        </span>
                      )}
                      {isToday && !sel && !av && (
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-500" />
                      )}
                    </button>
                  )
                })}
              </div>

              {/* Legenda — co znamená číslo pod dnem */}
              {Object.keys(monthAvail).length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-ink-700">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 border border-ink-900/15 bg-white inline-block" />
                    volno — číslo = {kind === 'hourly' ? 'volné hodiny' : isKemp ? 'volné sloty' : 'volné jednotky'}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 border border-amber-300 bg-amber-50 inline-block" />
                    poslední místa
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 border border-ink-900/10 bg-ink-900/[0.06] inline-block" />
                    obsazeno
                  </span>
                </div>
              )}

              {fullNightInRange() && (
                <div className="mt-3 bg-brand-50 border border-brand-200 text-brand-700 text-[13px] px-3 py-2">
                  <b>{czDate(fullNightInRange()!)}</b> je obsazeno — vyberte termín bez přeškrtnutých dnů.
                </div>
              )}

              <div className="mt-5 text-sm text-ink-700">
                {isKemp ? (
                  !from ? <>Vyberte den příjezdu — pro delší pobyt klikněte i na den odjezdu.</>
                  : !to ? <>Příjezd: <b className="text-ink-900">{czDate(from)}</b> · klikněte na den odjezdu, nebo pokračujte (1 slot)</>
                  : <>Příjezd: <b className="text-ink-900">{czDate(from)}</b> · Odjezd: <b className="text-ink-900">{czDate(to)}</b> · časy zvolíte v dalším kroku</>
                ) : kind === 'hourly' ? (
                  from ? <>Datum: <b className="text-ink-900">{czDate(from)}</b></>
                       : <>Vyberte datum rezervace.</>
                ) : (
                  <>
                    {from && !to && <>Příjezd: <b className="text-ink-900">{czDate(from)}</b> · Klikněte na datum odjezdu</>}
                    {from && to   && <>Příjezd: <b className="text-ink-900">{czDate(from)}</b> · Odjezd: <b className="text-ink-900">{czDate(to)}</b> · {nightsBetween(from, to)} {nightSuffix(nightsBetween(from, to))}</>}
                    {!from         && <>Vyberte datum příjezdu.</>}
                    {isTiny && (
                      <div className={`mt-2 text-[13px] font-semibold ${from && to && nightsSelected() < 2 ? 'text-brand-600' : 'text-ink-700/80'}`}>
                        Tiny House rezervujeme minimálně na 2&nbsp;noci.
                      </div>
                    )}
                  </>
                )}
              </div>

              {STAY_TIMES[type] && (
                <p className="mt-3 text-[12px] text-ink-700/80">{STAY_TIMES[type]}</p>
              )}
            </div>
          )}

          {/* KROK 1 · bivak — slot příjezdu a odjezdu hned pod kalendářem */}
          {step === 'calendar' && isKemp && from && (
            <div className="mt-6 pt-5 border-t border-ink-900/10">
              <p className="text-sm text-ink-700 mb-1">
                Bivak se rezervuje po <b className="text-ink-900">12hodinových slotech</b> — den (7:00–19:00) i noc (19:00–7:00).
              </p>
              <p className="text-[11px] text-ink-700/70 mb-4">
                Můžete zůstat i několik dní — vyberte čas příjezdu a odjezdu.
              </p>

              <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-brand-500 mb-1.5">
                Příjezd · {from ? czDate(from) : ''}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {KEMP_SLOTS.map((sl) => (
                  <button
                    key={'a' + sl.start}
                    onClick={() => setKempArrive(sl.start)}
                    className={`px-3 py-3 border text-left transition ${
                      kempArrive === sl.start ? 'bg-brand-500 text-white border-brand-500'
                                              : 'bg-white border-ink-900/15 hover:border-brand-500 text-ink-900'}`}
                  >
                    <div className="font-bold text-sm">{sl.start}</div>
                    <div className={`text-[11px] ${kempArrive === sl.start ? 'text-white/85' : 'text-ink-700'}`}>
                      {sl.start === '07:00' ? 'ráno' : 'večer'}
                    </div>
                  </button>
                ))}
              </div>

              <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-brand-500 mt-4 mb-1.5">
                Odjezd · {czDate(to || from!)}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {KEMP_SLOTS.map((sl) => (
                  <button
                    key={'d' + sl.start}
                    onClick={() => setKempDepart(sl.start === '07:00' ? '07:00' : '19:00')}
                    className={`px-3 py-3 border text-left transition ${
                      kempDepart === sl.start ? 'bg-brand-500 text-white border-brand-500'
                                              : 'bg-white border-ink-900/15 hover:border-brand-500 text-ink-900'}`}
                  >
                    <div className="font-bold text-sm">{sl.start}</div>
                    <div className={`text-[11px] ${kempDepart === sl.start ? 'text-white/85' : 'text-ink-700'}`}>
                      {sl.start === '07:00' ? 'ráno' : 'večer'}
                    </div>
                  </button>
                ))}
              </div>

              {kempBlocks() > 0 ? (
                <div className="mt-4 bg-brand-50 border border-brand-200 px-3 py-2 text-xs">
                  <b className="text-brand-600">{kempBlocks()}× 12 h</b>
                  <span className="text-ink-700 ml-1">
                    — {czDate(from!)} {kempArrive} → {czDate(to || from!)} {kempDepart}
                  </span>
                </div>
              ) : (
                <div className="mt-4 bg-brand-50 border border-brand-200 px-3 py-2 text-xs text-brand-600">
                  Odjezd musí být po příjezdu — vyberte v kalendáři i den odjezdu, nebo změňte časy.
                </div>
              )}

            </div>
          )}

          {/* KROK 1 · sport/sauna — mřížka kurt × čas hned pod kalendářem */}
          {step === 'calendar' && kind === 'hourly' && from && (
            <div className="mt-6 pt-5 border-t border-ink-900/10">
              <div className="mb-4 pb-3 border-b border-ink-900/8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-500 font-bold flex items-center gap-2">
                  <span className="inline-block w-5 h-px bg-brand-500" />
                  Vybraný den
                </div>
                <div className="text-xl md:text-2xl font-light text-ink-900 mt-1.5 tracking-tight">
                  {czDate(from!)}
                </div>
                <p className="text-[12px] text-ink-700/70 mt-2 leading-snug">
                  {type === 'sauna'
                    ? <>Procedura trvá <span className="text-ink-900">120 minut</span> — vyberte blok. Sauna je po celou dobu jen pro vás.</>
                    : <>Vyberte začátek v mřížce. <span className="text-ink-900">Pro skupinu</span> označte víc {resourceCategoryLabel(type)} ve stejném čase.</>}
                </p>
                {type !== 'sauna' && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-700/70">Délka</span>
                    {[1, 2, 3, 4].map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => { setHours(h); setSlot(null); setResourceIds([]) }}
                        className={`px-3 py-1.5 text-[12px] font-bold border transition-colors ${hours === h ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-ink-800 border-ink-900/15 hover:border-brand-400'}`}
                      >
                        {h} h
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {resourcesLoading ? (
                <div className="text-center py-8 text-ink-700 text-sm">Načítám obsazenost…</div>
              ) : resources.length === 0 ? (
                <div className="bg-brand-50 text-brand-600 p-3 text-sm">
                  Žádný {resourceCategoryLabel(type)} není definovaný. Zavolejte recepci.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto -mx-1 px-1">
                    <div className="min-w-[280px]">
                      {/* hlavička — názvy kurtů */}
                      <div
                        className="grid gap-1 mb-1"
                        style={{ gridTemplateColumns: `66px repeat(${resources.length}, minmax(0,1fr))` }}
                      >
                        <div className="text-[10px] uppercase tracking-wider text-ink-700/60 font-bold flex items-end pb-1">Čas</div>
                        {resources.map((r) => (
                          <div key={r.id} className="text-center text-[11px] font-bold text-ink-900 leading-tight pb-1">
                            {r.label}
                          </div>
                        ))}
                      </div>
                      {/* řádky — časové úseky */}
                      {slotsForType(type).filter((s) => type === 'sauna' || addHours(s.start, hours) <= SPORT_CLOSE).map((s) => {
                        // vícehodinová rezervace: řádek = začátek, blok trvá `hours` hodin a musí skončit do zavíračky
                        // (začátky, které by přesáhly 21:00, se vůbec nenabízejí)
                        const spanEnd = type === 'sauna' ? s.end : addHours(s.start, hours)
                        const beyond = false
                        const sid = `${s.start}-${spanEnd}`
                        return (
                          <div
                            key={sid}
                            className="grid gap-1 mb-1"
                            style={{ gridTemplateColumns: `66px repeat(${resources.length}, minmax(0,1fr))` }}
                          >
                            <div className="text-[11px] text-ink-800 font-semibold flex items-center tabular-nums">
                              {s.start}–{spanEnd}
                            </div>
                            {resources.map((r) => {
                              const taken = beyond || slotBooked(r, { start: s.start, end: spanEnd })
                              const sel = slot === sid && resourceIds.includes(r.id)
                              return (
                                <button
                                  key={r.id}
                                  disabled={taken}
                                  onClick={() => pickCell(r.id, sid)}
                                  className={`relative py-2.5 text-[11px] font-bold uppercase tracking-wider border transition-all duration-150 ease-out active:scale-95 ${
                                    taken
                                      ? 'bg-ink-900/10 text-ink-700/40 border-transparent cursor-not-allowed'
                                      : sel
                                        ? 'bg-gradient-to-br from-brand-500 to-brand-700 text-white border-brand-700 shadow-md shadow-brand-700/30 scale-[1.03]'
                                        : 'bg-white text-ink-800 border-ink-900/15 hover:border-brand-400 hover:bg-brand-50 hover:-translate-y-0.5'
                                  }`}
                                >
                                  {taken ? '✕' : sel ? '✓' : '•'}
                                  {/* Spodní capacity stripe — booked = červená čára, volný = jemný */}
                                  <span
                                    className={`absolute left-0 right-0 bottom-0 h-[2px] ${
                                      taken ? 'bg-brand-300/60' : sel ? 'bg-brand-300' : 'bg-forest-300/40'
                                    }`}
                                  />
                                </button>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  {slot && resourceIds.length > 0 && (
                    <div className="mt-3 bg-brand-50 border border-brand-200 px-3 py-2 text-xs">
                      <b className="text-brand-600">{slot.replace('-', ' – ')}</b>
                      <span className="text-ink-700 ml-1">
                        — {resources.filter((r) => resourceIds.includes(r.id)).map((r) => r.label).join(', ')}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* KROK 1 · výběr konkrétní jednotky hned pod kalendářem (po výběru termínu) */}
          {step === 'calendar' && showUnitPicker && (
            <div className="mt-6 pt-5 border-t border-ink-900/10">
              <p className="text-sm text-ink-700 mb-1">
                Vyberte {resourceCategoryLabel(type)} pro váš termín.
              </p>
              {isCombined && (
                <p className="text-[11px] text-ink-700/70 mb-3">
                  Vyberte konkrétní jednotku — Tiny House nebo glamping.
                </p>
              )}
              {/* Mapa sektorů — bivaky (B) i rybářské chatky (CH) u vody */}
              {(isKemp || type === 'chatka') && (
                <div className="mb-3">
                  <button
                    onClick={() => setMapOpen((v) => !v)}
                    className="w-full flex items-center justify-between border border-ink-900/15 px-3 py-2 text-[12px] font-semibold text-ink-900 hover:border-brand-500 transition"
                  >
                    <span className="flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3z" /><path d="M9 3v15M15 6v15" />
                      </svg>
                      Mapa lovných míst
                    </span>
                    <span className="text-ink-700">{mapOpen ? '−' : '+'}</span>
                  </button>
                  {mapOpen && (
                    <a href="/img/mapa_lovna_mista.jpg?v=2026-10-13" target="_blank" rel="noopener" className="block mt-2">
                      <img
                        src="/img/mapa_lovna_mista.jpg?v=2026-10-13"
                        alt="Mapa lovných míst — bivaková místa B1–B7, lovná místa u chatek CH1–CH10"
                        className="w-full border border-ink-900/10"
                        loading="lazy"
                      />
                      <span className="block mt-1 text-[11px] text-ink-700/70">
                        Zelené sektory = bivaková místa (B1–B7), červené = lovná místa u chatek (CH1–CH10). Klikněte pro zvětšení.
                      </span>
                    </a>
                  )}
                </div>
              )}
              {resourcesLoading ? (
                <div className="text-center py-8 text-ink-700 text-sm">Načítám dostupnost…</div>
              ) : resources.length === 0 ? (
                <div className="bg-brand-50 text-brand-600 p-3 text-sm">
                  Žádný {resourceCategoryLabel(type)} není definovaný. Zavolejte recepci.
                </div>
              ) : resources.filter(r => r.available).length === 0 ? (
                <div className="bg-brand-50 text-brand-600 p-3 text-sm">
                  Pro vybraný termín už není volné místo. Zkuste prosím jiný termín nebo zavolejte recepci.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 max-h-[45vh] overflow-y-auto">
                    {resources
                      .filter((r) => r.available)
                      .map((r) => {
                        const sel = resourceIds.includes(r.id)
                        return (
                          <button
                            key={r.id}
                            onClick={() => toggleResource(r.id)}
                            className={`px-3 py-3 border text-left transition relative ${
                              sel ? 'bg-brand-500 text-white border-brand-500'
                                  : 'bg-white border-ink-900/15 hover:border-brand-500 text-ink-900'
                            }`}
                          >
                            <div className={`absolute top-2 right-2 w-5 h-5 border-2 grid place-items-center text-xs font-bold ${
                              sel ? 'border-white bg-white text-brand-500' : 'border-ink-900/30'
                            }`}>
                              {sel ? '✓' : ''}
                            </div>
                            <div className="font-semibold text-sm pr-7">{r.label}</div>
                            <div className={`text-[10px] uppercase tracking-wider mt-1 font-bold ${
                              sel ? 'text-white/85' : 'text-brand-500'
                            }`}>
                              až {r.capacity_persons} os.
                            </div>
                          </button>
                        )
                      })}
                  </div>
                  {resourceIds.length > 0 && (
                    <div className="mt-3 bg-brand-50 border border-brand-200 px-3 py-2 text-xs">
                      <b className="text-brand-600">Vybráno: {resourceIds.length}</b>
                      <span className="text-ink-700 ml-1">
                        — celkem až {
                          resources
                            .filter((r) => resourceIds.includes(r.id))
                            .reduce((s, r) => s + r.capacity_persons, 0)
                        } osob
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* KROK 1 · společné tlačítko — až úplně dole pod vším, co je k výběru */}
          {step === 'calendar' && (
            <div className="mt-6">
              {step1Hint() && (
                <p className="mb-2 text-[12px] text-ink-700/80">{step1Hint()}</p>
              )}
              <button
                disabled={!canProceedStep1()}
                onClick={proceedStep1}
                className="btn-book w-full disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {kind === 'hourly' && cart.length > 0 ? 'Přidat termín a pokračovat →' : 'Pokračovat k údajům →'}
                {resourceIds.length > 1 ? ` (${resourceIds.length})` : ''}
              </button>
            </div>
          )}

          {/* KROK 2: Údaje & potvrzení */}
          {step === 'form' && (
            <div>
              <p className="text-sm text-ink-700 mb-4">
                Stačí jen <b className="text-ink-900">jméno a telefon</b>. Zbytek doladíme.
              </p>
              {kind === 'hourly' && (
                <div className="mb-5 border border-ink-900/10 overflow-hidden">
                  <div className="relative bg-gradient-to-r from-steel-800 to-steel-600 text-white px-4 py-2.5 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2">
                      <span className="inline-block w-4 h-px bg-brand-400" />
                      Vybrané termíny
                    </div>
                    <span className="bg-brand-500 text-white w-6 h-6 grid place-items-center text-xs font-bold rounded-full shadow-md shadow-brand-700/40">
                      {cart.length}
                    </span>
                  </div>
                  <div className="divide-y divide-ink-900/8 bg-gradient-to-b from-white to-steel-50/30">
                    {cart.map((c, i) => (
                      <div key={i} className="group px-4 py-3 flex items-center justify-between gap-3 text-sm hover:bg-brand-50/40 transition-colors">
                        <div className="w-9 h-9 rounded-lg bg-brand-50 border border-brand-100 flex items-center justify-center text-brand-600 shrink-0">
                          <svg viewBox="0 0 24 24" className="w-4.5 h-4.5" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="5" width="18" height="16" rx="2" />
                            <path d="M8 3v4M16 3v4M3 10h18" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-ink-900 text-[14px] leading-tight">
                            {czDate(c.date)}
                          </div>
                          <div className="text-[11px] text-ink-700 mt-0.5 font-mono tabular-nums">
                            {c.slot.replace('-', ' – ')} · {c.resourceLabels}
                          </div>
                        </div>
                        <button
                          onClick={() => removeCartItem(i)}
                          className="shrink-0 w-7 h-7 grid place-items-center text-ink-700/60 hover:text-white hover:bg-brand-500 border border-transparent hover:border-brand-500 transition-colors text-sm"
                          aria-label="Odebrat termín"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={addAnotherDay}
                    className="w-full border-t border-ink-900/10 px-3 py-3 text-brand-600 hover:bg-brand-50 font-bold uppercase tracking-[0.15em] text-[11px] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <span className="text-base leading-none">+</span> Přidat další den
                  </button>
                </div>
              )}
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.20em] text-brand-500 font-bold">Jméno a příjmení *</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 w-full border border-ink-900/15 px-3.5 py-3 text-ink-800 bg-white transition-shadow focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_4px_rgba(229,2,69,0.12)]"
                  placeholder="Jan Novák"
                />
              </label>
              <label className="block mt-4">
                <span className="text-[10px] uppercase tracking-[0.20em] text-brand-500 font-bold">Telefon *</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1.5 w-full border border-ink-900/15 px-3.5 py-3 text-ink-800 bg-white transition-shadow focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_4px_rgba(229,2,69,0.12)]"
                  placeholder="+420 ___ ___ ___"
                />
              </label>
              {kind !== 'hourly' && (
                <label className="block mt-4">
                  <span className="text-[10px] uppercase tracking-[0.20em] text-brand-500 font-bold">E-mail *</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full border border-ink-900/15 px-3.5 py-3 text-ink-800 bg-white transition-shadow focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_4px_rgba(229,2,69,0.12)]"
                    placeholder="jan.novak@email.cz"
                  />
                  <span className="mt-1 block text-[11px] text-ink-700/80">Pošleme na něj potvrzení a pokyny k pobytu.</span>
                </label>
              )}
              {kind === 'stay' && (
                <label className="block mt-4">
                  <span className="text-[10px] uppercase tracking-[0.20em] text-brand-500 font-bold">Počet hostů</span>
                  <select
                    value={String(guests)}
                    onChange={(e) => setPersons(e.target.value)}
                    className="mt-1.5 w-full border border-ink-900/15 px-3.5 py-3 text-ink-800 bg-white transition-shadow focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_4px_rgba(229,2,69,0.12)]"
                  >
                    {Array.from({ length: maxGuests }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} {n===1?'host':n<5?'hosté':'hostů'}</option>
                    ))}
                  </select>
                  <span className="block mt-1 text-[11px] text-ink-700/70">
                    {resourceIds.length > 1
                      ? `Vybráno ${resourceIds.length} jednotek — dohromady max. ${maxGuests} hostů.`
                      : `Kapacita: max. ${maxGuests} ${maxGuests === 1 ? 'host' : maxGuests < 5 ? 'hosté' : 'hostů'}.`}
                  </span>
                </label>
              )}

              {(type === 'karavan' || type === 'kemp') && (
                <label className="block mt-4 cursor-pointer">
                  <div className="flex items-start gap-3 border border-ink-900/15 p-3 hover:bg-brand-50/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={electricity}
                      onChange={(e) => setElectricity(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-brand-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-bold text-ink-900 flex items-center gap-1.5">
                        <span>⚡</span> El. přípojka
                      </div>
                      <div className="text-[11px] text-ink-700/80 mt-0.5">
                        +{type === 'karavan'
                          ? `${priceList.el_karavan_night ?? 200} Kč / 24 h (noc)`
                          : `${priceList.el_kemp_12h ?? 100} Kč / 12 h slot`}
                      </div>
                    </div>
                  </div>
                </label>
              )}

              {(type === 'chatka' || type === 'kemp') && (   /* i bivak (Dominik 25. 10.: vanička, podběrák, pouštěcí vak) */
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-ink-700/70 font-semibold mb-1.5">
                    Zapůjčení vybavení (volitelné)
                  </div>
                  <div className="border border-ink-900/15 rounded-lg divide-y divide-ink-900/10">
                    {[
                      { id: 'podberak', label: 'Podběrák', price: priceList.addon_podberak ?? 200 },
                      { id: 'vanicka', label: 'Vanička', price: priceList.addon_vanicka ?? 200 },
                      { id: 'sak', label: 'Pouštěcí vak', price: priceList.addon_sak ?? 100 },
                    ].map((a) => (
                      <label key={a.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-brand-50/30 transition-colors">
                        <input
                          type="checkbox"
                          checked={addons.includes(a.id)}
                          onChange={(e) =>
                            setAddons((prev) => e.target.checked ? [...prev, a.id] : prev.filter((x) => x !== a.id))
                          }
                          className="w-4 h-4 accent-brand-500"
                        />
                        <span className="flex-1 text-sm font-semibold text-ink-900">{a.label}</span>
                        <span className="text-[12px] text-ink-700/80">+{a.price} Kč / 24 h</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* v2: voucher (loyalty / weather) — sbalitelné */}
              <div className="mt-4 border border-ink-900/10">
                <button
                  type="button"
                  onClick={() => setVoucherOpen((v) => !v)}
                  className="w-full px-3.5 py-2.5 flex items-center justify-between text-[11px] uppercase tracking-[0.15em] font-bold text-ink-800 hover:bg-brand-50/50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-brand-500">🎟</span> Mám voucher / promokód
                  </span>
                  <span className="text-ink-700/60">{voucherOpen ? '−' : '+'}</span>
                </button>
                {voucherOpen && (
                  <div className="px-3.5 pb-3.5 pt-1 border-t border-ink-900/8">
                    <input
                      type="text"
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                      placeholder="kód z SMS nebo promokód"
                      className="w-full border border-ink-900/15 px-3.5 py-2.5 text-ink-800 bg-white font-mono tracking-wider uppercase text-sm focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_4px_rgba(229,2,69,0.12)] transition-shadow"
                    />
                    <p className="text-[11px] text-ink-700/70 mt-2 leading-snug">
                      Kód z SMS (počasí · věrnostka 5×) nebo promokód. Sleva se uplatní po odeslání.
                    </p>
                  </div>
                )}
              </div>

              {/* Souhrn přímo pod údaji — žádný další krok */}
              <h3 className="mt-6 font-semibold text-ink-900 uppercase tracking-wider text-sm">Souhrn rezervace</h3>
              <dl className="mt-3 divide-y divide-ink-900/8 border-y border-ink-900/10">
                <Row k="Druh" v={label} />
                {kind === 'hourly' ? (
                  cart.map((c, i) => (
                    <Row
                      key={i}
                      k={cart.length > 1 ? `Termín ${i + 1}` : 'Termín'}
                      v={`${czDate(c.date)} · ${c.slot.replace('-', ' – ')} · ${c.resourceLabels}`}
                    />
                  ))
                ) : isKemp ? (
                  <>
                    {resourceLabels && <Row k="Místo" v={resourceLabels} />}
                    <Row k="Příjezd" v={`${czDate(from!)} · ${kempArrive}`} />
                    <Row k="Odjezd" v={`${czDate(to || from!)} · ${kempDepart}`} />
                    <Row k="Délka" v={`${kempBlocks()}× 12 h`} />
                  </>
                ) : (
                  <>
                    {resourceLabels && <Row k={resourceIds.length > 1 ? `Vybráno · ${resourceIds.length}×` : 'Konkrétně'} v={resourceLabels} />}
                    <Row k="Příjezd" v={`${czDate(from!)}${STAY_CHECKIN[type] ? ` · ${STAY_CHECKIN[type]}` : ''}`} />
                    {to && <Row k="Odjezd" v={`${czDate(to)}${STAY_CHECKOUT[type] ? ` · ${STAY_CHECKOUT[type]}` : ''}`} />}
                  </>
                )}
                {quote && <Row k="Cena" v={`${quote.price_czk.toLocaleString('cs')} Kč`} />}
              </dl>

              {kind === 'hourly' ? (
                <p className="mt-4 text-[13px] text-ink-800 leading-relaxed">
                  Sportovní rezervace se hradí <b>100 % předem kartou</b>. Po odeslání máte
                  <b> 50 minut</b> na dokončení platby — pak se slot uvolní.
                </p>
              ) : (
                <p className="mt-4 text-[13px] text-ink-800 leading-relaxed">
                  Zaplaťte hned kartou — rezervace je okamžitě potvrzená a termín pevně držíme.
                </p>
              )}

              {err && <div className="mt-3 text-brand-600 text-sm font-semibold">{err}</div>}

              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => (kind === 'hourly' ? backFromHourlyForm() : setStep('calendar'))}
                  className="btn-outline-dark flex-1 text-xs py-2.5"
                >
                  ← Zpět
                </button>
                <button
                  disabled={busy || !canSubmitForm() || (kind === 'hourly' && cart.length === 0)}
                  onClick={() => submit(true)}
                  className="btn-book flex-[2] disabled:opacity-40 shadow-lg shadow-brand-700/20"
                >
                  {busy ? 'Odesílám…' : 'Závazně rezervovat →'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4.5: Platební metoda — fake QR + brány (před ostrým spuštěním brány) */}
          {step === 'payment' && (
            <PaymentMethodStep
              priceCzk={estimatedCzk}
              variableSymbol={resMeta.variable_symbol}
              reservationId={reservationId}
              onContinue={() => setStep('done')}
            />
          )}

          {/* STEP 5: Done — potvrzení rezervace (VS, reschedule, voucher) */}
          {step === 'done' && (
            <div className="py-2">
              <div className="text-center relative">
                {/* Subtle particle burst — 6 dots */}
                <div className="absolute left-1/2 top-12 -translate-x-1/2 pointer-events-none">
                  <span className="absolute w-1.5 h-1.5 rounded-full bg-brand-400" style={{ animation: 'float 3s ease-in-out infinite', top: '-22px', left: '-30px' }} />
                  <span className="absolute w-1 h-1 rounded-full bg-brand-300" style={{ animation: 'float 3.2s ease-in-out infinite 0.3s', top: '-18px', left: '32px' }} />
                  <span className="absolute w-1 h-1 rounded-full bg-forest-400" style={{ animation: 'float 2.8s ease-in-out infinite 0.5s', top: '32px', left: '-36px' }} />
                  <span className="absolute w-1.5 h-1.5 rounded-full bg-sun-400" style={{ animation: 'float 3.5s ease-in-out infinite 0.7s', top: '34px', left: '34px' }} />
                  <span className="absolute w-1 h-1 rounded-full bg-brand-500" style={{ animation: 'float 3.1s ease-in-out infinite 0.2s', top: '6px', left: '-46px' }} />
                  <span className="absolute w-1 h-1 rounded-full bg-forest-500" style={{ animation: 'float 2.9s ease-in-out infinite 0.4s', top: '4px', left: '44px' }} />
                </div>
                <div className="relative mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200 grid place-items-center mb-5 shadow-[0_12px_40px_-8px_rgba(229,2,69,0.45)]">
                  <span className="text-5xl text-brand-600 font-bold animate-fade-up">✓</span>
                </div>
                <h3 className="font-semibold text-xl text-ink-900 uppercase tracking-wider">
                  {resMeta.voucher_pct === 100 ? 'Rezervace potvrzena' : 'Rezervace přijata'}
                </h3>
                <p className="text-ink-700 mt-2 text-sm">
                  {resMeta.voucher_pct === 100
                    ? <>Voucher byl uplatněn — termín máte rezervovaný zdarma.</>
                    : <>Číslo rezervace <b className="text-ink-900">#{reservationId}</b>.</>
                  }
                </p>
              </div>

              {/* Bank/payment info — variabilní symbol pro rozlišení plateb */}
              {resMeta.variable_symbol && (
                <div className="mt-5 bg-gradient-to-br from-steel-50 to-white border border-steel-200 p-4">
                  <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-steel-700 flex items-center gap-2">
                    <span className="inline-block w-4 h-px bg-steel-600" />
                    Variabilní symbol
                  </div>
                  <div className="mt-1 font-mono text-2xl font-bold text-ink-900 tracking-wider">
                    {resMeta.variable_symbol}
                  </div>
                  <div className="mt-1 text-[11px] text-ink-700/70">
                    Pro rozpoznání platby a vašeho termínu.
                  </div>
                </div>
              )}

              {estimatedCzk > 0 && (
                <p className="text-ink-700 mt-4 text-sm text-center">
                  Cena celkem: <b className="text-ink-900">{estimatedCzk.toLocaleString('cs')} Kč</b>
                </p>
              )}

              {/* Reschedule odkaz — sport, do 24h před */}
              {resMeta.reschedule_token && reservationId && (
                <div className="mt-5 bg-gradient-to-br from-forest-50 to-white border border-forest-200 p-4">
                  <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-forest-700 flex items-center gap-2">
                    <span className="inline-block w-4 h-px bg-forest-600" />
                    Potřebujete přesunout termín?
                  </div>
                  <p className="mt-1.5 text-sm text-ink-900 leading-snug">
                    Použijte odkaz ze SMS nebo otevřete přímo:
                  </p>
                  <a
                    href={`#preplanovat?id=${reservationId}&t=${resMeta.reschedule_token}`}
                    className="mt-2 inline-flex items-center gap-2 text-forest-700 font-bold uppercase tracking-wider text-xs border border-forest-300 hover:bg-forest-600 hover:text-white hover:border-forest-600 px-3 py-2 transition-colors"
                  >
                    Přesunout rezervaci →
                  </a>
                  <p className="mt-2 text-[11px] text-ink-700/70 leading-snug">
                    Nejpozději 24 hodin před původním termínem. 1× zdarma.
                  </p>
                </div>
              )}

              <p className="text-ink-700 mt-5 text-xs text-center">
                Potvrzení vám pošleme SMS na <b>{phone}</b>.
              </p>

              <div className="mt-5 flex flex-col gap-2">
                <button onClick={close} className="btn-book w-full">Hotovo</button>
                <a
                  href="#pravidla"
                  className="text-ink-700 text-[11px] uppercase tracking-[0.10em] font-semibold text-center hover:text-brand-500 transition-colors"
                >
                  Pravidla rezervace · věrnostka
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="py-2.5 flex items-baseline justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-[0.15em] text-ink-700 font-semibold">{k}</dt>
      <dd className="text-ink-900 font-semibold text-sm text-right">{v}</dd>
    </div>
  )
}

function stepLabel(s: string, kind: Kind): string {
  // Step labels jsou jen pro hosta — orientační; přesné číslování se mění
  // dle toho, jestli má daný typ resource krok.
  return ({
    calendar: kind === 'hourly' ? 'Datum & čas' : 'Termín & místo',
    form: 'Údaje & potvrzení',
    payment: 'Platba',
    done: 'Hotovo',
  } as Record<string, string>)[s] || s
}

function resourceCategoryLabel(t: string): string {
  return ({
    penzion: 'pokoje', chatka: 'chatky', tinyhouse: 'Tiny House',
    glamping: 'glamping', glamping_tiny: 'ubytování', karavan: 'stání', kemp: 'místo pro bivak', tenis: 'kurty',
    beach: 'hřiště', sauna: 'saunu', akce: 'prostor',
  } as Record<string, string>)[t] || 'zdroje'
}

function buildMonth(year: number, month: number): ({ iso: string; day: number } | null)[] {
  const first = new Date(year, month, 1)
  const startWeekday = (first.getDay() + 6) % 7 // pondělí = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: ({ iso: string; day: number } | null)[] = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push({ iso, day: d })
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

function czDate(iso: string) {
  const [y, m, d] = iso.split('-')
  return `${parseInt(d)}. ${parseInt(m)}. ${y}`
}

function nightsBetween(a: string, b: string) {
  const A = new Date(a).getTime()
  const B = new Date(b).getTime()
  return Math.max(1, Math.round((B - A) / 86400000))
}
function nightSuffix(n: number) {
  if (n === 1) return 'noc'
  if (n < 5) return 'noci'
  return 'nocí'
}
